import React from 'react'
import PropTypes from 'prop-types'
import useRegistroCliente from '../hooks/useRegistroCliente'

/**
 * Componente principal para el registro de clientes
 * Implementación completamente nueva sin reutilizar código existente
 */
const RegistroClienteForm = ({ onBack }) => {
  const {
    // Estado del formulario
    datosFormulario,
    paso,
    enviando,
    cargandoDatos,
    aceptoTerminos,
    setAceptoTerminos,

    // Datos de referencia
    opcionesPlanesConfig,
    planesCompletos,
    planSeleccionado,
    distritos,
    entidadesExternas,

    // Estados de validación
    erroresValidacion,
    validandoDisponibilidad,

    // Funciones de navegación
    avanzarSiguientePaso,
    volverPasoAnterior,

    // Funciones de manejo de datos
    manejarCambioInput,
    manejarSeleccionTipoPlan,
    manejarSeleccionPlanEspecifico,
    enviarSolicitudRegistro
  } = useRegistroCliente(onBack)

  // Paso 1: Selección de plan
  if (paso === 1) {
    return (
      <div className="min-h-screen gradient-helpmed flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-heavy p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  <path d="M19 13h-2v-2h2v2zm-2 2h2v2h-2v-2z"/>
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Solicitud de Registro</h2>
              <p className="text-gray-600 text-lg">Completa el formulario para solicitar tu membresía</p>
            </div>

            {cargandoDatos ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Cargando opciones de planes...</p>
              </div>
            ) : (
              <>
                {/* Área de selección de plan */}
                <div className="bg-blue-50 rounded-lg p-6 mb-6 border border-blue-200">
                  <div className="flex items-center mb-4">
                    <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-blue-800">Tipo de Plan Solicitado</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2">
                        Tipo de Plan <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={datosFormulario.tipo_plan}
                        onChange={(e) => manejarSeleccionTipoPlan(e.target.value)}
                        className="w-full p-4 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                      >
                        <option value="">Selecciona un tipo de plan</option>
                        <option value="FAMILIAR">Plan Familiar</option>
                        <option value="CORPORATIVO">Plan Corporativo</option>
                        <option value="EXTERNO">Afiliado Externo</option>
                      </select>
                      <p className="text-sm text-blue-600 mt-2">
                        Por favor, selecciona primero el tipo de plan para continuar con el registro.
                      </p>
                    </div>

                    {/* Selector de plan específico */}
                    {datosFormulario.tipo_plan && planesCompletos.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">
                          Plan Específico <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={datosFormulario.subtipo_plan}
                          onChange={(e) => manejarSeleccionPlanEspecifico(e.target.value)}
                          className="w-full p-4 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                        >
                          <option value="">Selecciona un plan específico</option>
                          {planesCompletos.map((plan) => (
                            <option key={plan.codigo} value={plan.codigo}>
                              {plan.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Botones de acción */}
                <div className="flex space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={onBack}
                    className="flex-1 px-6 py-4 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={avanzarSiguientePaso}
                    disabled={!datosFormulario.tipo_plan || !datosFormulario.subtipo_plan || cargandoDatos}
                    className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    Continuar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Paso 2: Formulario completo
  return (
    <div className="min-h-screen gradient-helpmed flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-2xl shadow-heavy p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Solicitud de Registro</h2>
            <p className="text-gray-600">
              Completa el formulario para solicitar tu membresía
            </p>
            {planSeleccionado && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Plan seleccionado:</strong> {planSeleccionado.nombre}
                </p>
              </div>
            )}
          </div>

          <form onSubmit={enviarSolicitudRegistro} className="space-y-6">
            {/* Datos Personales */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Datos Personales</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombres <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={datosFormulario.nombre}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.nombre ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ingresa tu nombre"
                  />
                  {erroresValidacion.nombre && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.nombre}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apellidos <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="apellidos"
                    value={datosFormulario.apellidos}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.apellidos ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ingresa tus apellidos"
                  />
                  {erroresValidacion.apellidos && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.apellidos}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DNI <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="dni"
                    value={datosFormulario.dni}
                    onChange={manejarCambioInput}
                    maxLength="8"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.dni ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="12345678"
                  />
                  {erroresValidacion.dni && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.dni}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Nacimiento <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="fecha_nacimiento"
                    value={datosFormulario.fecha_nacimiento}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.fecha_nacimiento ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {erroresValidacion.fecha_nacimiento && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.fecha_nacimiento}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Información de Contacto */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Información de Contacto</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={datosFormulario.email}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="ejemplo@correo.com"
                  />
                  {erroresValidacion.email && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={datosFormulario.telefono}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.telefono ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="987654321"
                  />
                  {erroresValidacion.telefono && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.telefono}</p>
                  )}
                </div>

                {/* Entidad Externa (SOLO SI EXTERNO) + Dirección - MISMA FILA */}
                {datosFormulario.tipo_plan === 'EXTERNO' ? (
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3">
                    <label className="block text-sm font-medium text-blue-900 mb-2 flex items-center">
                      <i className="fas fa-hospital text-blue-600 mr-2"></i>
                      Entidad Externa
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <select
                      name="id_entidad_externa"
                      value={datosFormulario.id_entidad_externa}
                      onChange={manejarCambioInput}
                      disabled={cargandoDatos || entidadesExternas.length === 0}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        erroresValidacion.id_entidad_externa ? 'border-red-500 bg-red-50' : 'border-blue-300 bg-white'
                      }`}
                    >
                      <option value="">
                        {cargandoDatos ? 'Cargando entidades...' :
                         entidadesExternas.length === 0 ? 'No hay entidades disponibles' :
                         'Selecciona tu institución médica'}
                      </option>
                      {entidadesExternas.map((entidad) => (
                        <option key={entidad.id} value={entidad.id}>
                          {entidad.nombre_entidad}
                        </option>
                      ))}
                    </select>
                    {erroresValidacion.id_entidad_externa && (
                      <p className="text-red-500 text-xs mt-1">{erroresValidacion.id_entidad_externa}</p>
                    )}
                    <p className="text-xs text-blue-700 mt-1 flex items-start">
                      <i className="fas fa-info-circle mr-1 mt-0.5"></i>
                      Selecciona la institución médica con la que tienes convenio
                    </p>
                  </div>
                ) : (
                  <div></div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="direccion"
                    value={datosFormulario.direccion}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.direccion ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Av. Principal 123, Urbanización..."
                  />
                  {erroresValidacion.direccion && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.direccion}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Distrito <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="distrito"
                    value={datosFormulario.distrito}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.distrito ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Seleccionar distrito</option>
                    {distritos.map((distrito) => (
                      <option key={distrito.id} value={distrito.id}>
                        {distrito.nombre}
                      </option>
                    ))}
                  </select>
                  {erroresValidacion.distrito && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.distrito}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ciudad <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="ciudad"
                    value={datosFormulario.ciudad}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.ciudad ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Lima, Arequipa, Cusco..."
                  />
                  {erroresValidacion.ciudad && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.ciudad}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contacto de Emergencia */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contacto de Emergencia</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nombre_contacto_emergencia"
                    value={datosFormulario.nombre_contacto_emergencia}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.nombre_contacto_emergencia ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nombre del contacto de emergencia"
                  />
                  {erroresValidacion.nombre_contacto_emergencia && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.nombre_contacto_emergencia}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="telefono_contacto_emergencia"
                    value={datosFormulario.telefono_contacto_emergencia}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.telefono_contacto_emergencia ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="987654321"
                  />
                  {erroresValidacion.telefono_contacto_emergencia && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.telefono_contacto_emergencia}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relación <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="relacion_contacto"
                    value={datosFormulario.relacion_contacto}
                    onChange={manejarCambioInput}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      erroresValidacion.relacion_contacto ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Seleccionar relación</option>
                    <option value="padre">Padre</option>
                    <option value="madre">Madre</option>
                    <option value="hermano">Hermano/a</option>
                    <option value="conyuge">Cónyuge</option>
                    <option value="hijo">Hijo/a</option>
                    <option value="familiar">Otro familiar</option>
                    <option value="amigo">Amigo/a</option>
                  </select>
                  {erroresValidacion.relacion_contacto && (
                    <p className="text-red-500 text-xs mt-1">{erroresValidacion.relacion_contacto}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Términos y Condiciones */}
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={aceptoTerminos}
                  onChange={(e) => setAceptoTerminos(e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  Acepto los{' '}
                  <a href="#" className="text-blue-600 hover:underline">
                    términos y condiciones
                  </a>{' '}
                  de Help MED y autorizo el tratamiento de mis datos personales según la{' '}
                  <a href="#" className="text-blue-600 hover:underline">
                    política de privacidad
                  </a>
                  .
                </span>
              </label>
              {erroresValidacion.terminos && (
                <p className="text-red-500 text-xs mt-2">{erroresValidacion.terminos}</p>
              )}
            </div>

            {/* Botones de acción */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={volverPasoAnterior}
                className="flex-1 px-4 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Volver
              </button>
              <button
                type="submit"
                disabled={enviando || validandoDisponibilidad}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {enviando ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enviando Solicitud...
                  </>
                ) : (
                  'Enviar Solicitud'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

RegistroClienteForm.propTypes = {
  onBack: PropTypes.func.isRequired
}

export default RegistroClienteForm